import type { QRSettings } from '../../types/qr'
import './QRPresets.scss'

interface QRPresetsProps {
  onSelectPreset: (preset: Partial<QRSettings>) => void
  currentSettings: QRSettings
}

interface Preset {
  id: string
  name: string
  image?: string
  settings: Partial<QRSettings>
}

const BRAND_PRESETS: Preset[] = [
  {
    id: 'egos-black',
    name: 'EGOS Classic',
    image: '/images/preset_brand_black.png',
    settings: {
      dotType: 'extra-rounded',
      cornerSquareType: 'extra-rounded',
      cornerDotType: 'dot',
      fgColor: '#000000',
      bgColor: '#ffffff',
      logoImage: '/images/egos_logo.svg',
      logoSize: 40,
      ecLevel: 'H',
    },
  },
  {
    id: 'egos-blue',
    name: 'EGOS Blue',
    image: '/images/preset_brand_blue.png',
    settings: {
      dotType: 'extra-rounded',
      cornerSquareType: 'extra-rounded',
      cornerDotType: 'dot',
      fgColor: '#557dbf',
      bgColor: '#ffffff',
      logoImage: '/images/egos_logo.svg',
      logoSize: 40,
      ecLevel: 'H',
    },
  },
  {
    id: 'egos-green',
    name: 'EGOS Green',
    image: '/images/preset_brand_green.png',
    settings: {
      dotType: 'extra-rounded',
      cornerSquareType: 'extra-rounded',
      cornerDotType: 'dot',
      fgColor: '#33a852',
      bgColor: '#ffffff',
      logoImage: '/images/egos_logo.svg',
      logoSize: 40,
      ecLevel: 'H',
    },
  },
  {
    id: 'egos-red',
    name: 'EGOS Red',
    image: '/images/preset_brand_red.png',
    settings: {
      dotType: 'extra-rounded',
      cornerSquareType: 'extra-rounded',
      cornerDotType: 'dot',
      fgColor: '#e94335',
      bgColor: '#ffffff',
      logoImage: '/images/egos_logo.svg',
      logoSize: 40,
      ecLevel: 'H',
    },
  },
]

const STYLE_PRESETS: Preset[] = [
  {
    id: 'classic',
    name: 'Classic',
    image: '/images/preset_classic.png',
    settings: {
      dotType: 'square',
      cornerSquareType: 'square',
      cornerDotType: 'square',
      fgColor: '#000000',
      bgColor: '#ffffff',
    },
  },
  {
    id: 'rounded',
    name: 'Rounded',
    image: '/images/preset_rounded.png',
    settings: {
      dotType: 'rounded',
      cornerSquareType: 'extra-rounded',
      cornerDotType: 'dot',
      fgColor: '#000000',
      bgColor: '#ffffff',
    },
  },
  {
    id: 'dots',
    name: 'Dots',
    image: '/images/preset_dots.png',
    settings: {
      dotType: 'dots',
      cornerSquareType: 'dot',
      cornerDotType: 'dot',
      fgColor: '#000000',
      bgColor: '#ffffff',
    },
  },
  {
    id: 'extra-smooth',
    name: 'Extra Smooth',
    image: '/images/preset_extra_smooth.png',
    settings: {
      dotType: 'extra-rounded',
      cornerSquareType: 'extra-rounded',
      cornerDotType: 'dot',
      fgColor: '#000000',
      bgColor: '#ffffff',
    },
  },
  {
    id: 'classy',
    name: 'Classy',
    image: '/images/preset_classy.png',
    settings: {
      dotType: 'classy',
      cornerSquareType: 'square',
      cornerDotType: 'square',
      fgColor: '#000000',
      bgColor: '#ffffff',
    },
  },
  {
    id: 'classy-rounded',
    name: 'Classy Rounded',
    image: '/images/preset_classy_rounded.png',
    settings: {
      dotType: 'classy-rounded',
      cornerSquareType: 'extra-rounded',
      cornerDotType: 'dot',
      fgColor: '#000000',
      bgColor: '#ffffff',
    },
  },
  {
    id: 'ocean-blue',
    name: 'Ocean Blue',
    image: '/images/preset_ocean_blue.png',
    settings: {
      dotType: 'rounded',
      cornerSquareType: 'extra-rounded',
      cornerDotType: 'dot',
      fgColor: '#1e40af',
      bgColor: '#ffffff',
    },
  },
  {
    id: 'dark-mode',
    name: 'Dark Mode',
    image: '/images/preset_dark_mode.png',
    settings: {
      dotType: 'extra-rounded',
      cornerSquareType: 'extra-rounded',
      cornerDotType: 'dot',
      fgColor: '#ffffff',
      bgColor: '#1f2937',
    },
  },
]

export function QRPresets({ onSelectPreset, currentSettings }: QRPresetsProps) {
  const isPresetActive = (preset: Preset) => {
    const matchesStyle = 
      currentSettings.dotType === preset.settings.dotType &&
      currentSettings.cornerSquareType === preset.settings.cornerSquareType &&
      currentSettings.cornerDotType === preset.settings.cornerDotType &&
      currentSettings.fgColor === preset.settings.fgColor &&
      currentSettings.bgColor === preset.settings.bgColor

    if (preset.settings.logoImage) {
      return matchesStyle && currentSettings.logoImage === preset.settings.logoImage
    }
    return matchesStyle
  }

  return (
    <div className="qr-presets">
      <div className="preset-group">
        <h3>Style Presets</h3>
        <div className="presets-grid">
          {STYLE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              className={`preset-btn ${isPresetActive(preset) ? 'active' : ''}`}
              onClick={() => onSelectPreset(preset.settings)}
              title={preset.name}
            >
              <img src={preset.image} alt={preset.name} className="preset-img" />
              <span className="preset-name">{preset.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="preset-group">
        <h3>Brand Presets</h3>
        <div className="presets-grid brand-grid">
          {BRAND_PRESETS.map((preset) => (
            <button
              key={preset.id}
              className={`preset-btn brand-preset ${isPresetActive(preset) ? 'active' : ''}`}
              onClick={() => onSelectPreset(preset.settings)}
              title={preset.name}
            >
              <img src={preset.image} alt={preset.name} className="preset-img" />
              <span className="preset-name">{preset.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
